import { useCallback, useContext, useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'

import { useMutation, useQueryClient } from '@tanstack/react-query'

import { FeedbackContext } from '~/components/feedback'
import ToggleInput from '~/components/Toggle'
import Tooltip from '~/components/Tooltip'
import { isPublisher } from '~/helpers'
import { useAPPTranslation } from '~/i18n/useAPPTranslation'

import {
  listCampaignSubpublishersKey,
  patchCampaignSubpublishersService
} from '../../services/campaign/subpublisher'

export const SUBPUBLISHER_LIST_REFETCH_DELAY_MS = 2000

const ToggleSubpublisher = ({ data }: ToggleSubpublisherProps) => {
  const { id } = useParams() as { id: CampaignId }
  const { t, tToasts } = useAPPTranslation()
  const { toast } = useContext(FeedbackContext)

  const isChecked = data.active

  const [active, setActive] = useState<boolean>(isChecked)

  const label = useMemo(
    () => (active ? t('c:words.active') : t('c:words.inactive')),
    [active, t]
  )

  /**
   * Handle mutate
   */
  const queryClient = useQueryClient()
  const refetchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const queryKey = [listCampaignSubpublishersKey, id]

  type MutationFnProps = {
    value: boolean
    handleLoading: ToogleOnChangeProps['handleLoading']
  }

  const { mutateAsync, isPending } = useMutation({
    mutationFn: async ({ value }: MutationFnProps) => {
      const payload = {
        subpublisherId: data.subpublisher_id,
        body: {
          campaign_id: id,
          active: value
        }
      }

      await patchCampaignSubpublishersService(payload)
    },
    onMutate: async ({ value, handleLoading }) => {
      return { value, handleLoading }
    },
    onSuccess: (data, variables, context) => {
      const toastKey = context?.value ? 'active' : 'inactive'

      tToasts({
        prefix: `rm:campaign.Subpublishers.patch.success.${toastKey}`,
        toastFunction: toast.success
      })

      context?.handleLoading(context.value)

      if (refetchTimeoutRef.current) {
        clearTimeout(refetchTimeoutRef.current)
      }

      refetchTimeoutRef.current = setTimeout(() => {
        queryClient.invalidateQueries({
          queryKey
        })
      }, SUBPUBLISHER_LIST_REFETCH_DELAY_MS)
    },
    onError: (error, variables, context) => {
      tToasts({
        prefix: `rm:campaign.Subpublishers.patch.error`,
        toastFunction: toast.critical
      })

      context?.handleLoading(!context.value)
    }
  })

  const handleChange = useCallback(
    ({ e, handleLoading }: ToogleOnChangeProps) => {
      const value = e.target.checked

      setActive(value)

      mutateAsync({
        handleLoading,
        value
      })
    },
    [mutateAsync]
  )

  const inputKey = `subpublisher_${data.id}`

  return (
    <Tooltip
      key={`toggle_${inputKey}`}
      interactiveTrigger
      text={label}
      icon={
        <ToggleInput
          key={inputKey}
          name={inputKey}
          isChecked={isChecked}
          onChange={handleChange}
          data-testid="toggleSubpublisher"
          disabled={isPending || isPublisher()}
          isAsync
          size="small"
        />
      }
    />
  )
}

export default ToggleSubpublisher
